import {failure} from '@danwang/either';
import {extract} from '@danwang/extractor';
import extractorError from '@danwang/extractor/lib/error';
import absurd from 'common/absurd';
import {DateTime} from 'luxon';
import {parseJson} from 'src/parseJson';
import type {ExtractResult, Extractor} from '@danwang/extractor';

export type VenmoResult = {
  amount: number;
  date: string;
  description: string;
  venmoId: string;
};

const parseVenmoDate = (venmoDate: string): string =>
  DateTime.fromISO(venmoDate, {zone: 'UTC'}).toLocal().toISODate();

const parseVenmoAmount = (amount: string): number => {
  const signString = amount[0];
  if (signString !== '+' && signString !== '-') {
    throw new Error(`Unexpected sign: ${amount}`);
  }
  const sign = signString === '+' ? 1 : -1;
  const numbers = amount.substring(3).replace(/[.,]/g, '');
  return parseInt(numbers, 10) * sign;
};

type VenmoPayment = {
  id: string;
  amount: string;
  date: string;
  title: {
    sender: {
      username: string;
      displayName: string;
    };
    receiver: {
      username: string;
      displayName: string;
    };
  };
  note: {content: string};
  type: 'payment';
};

const extractPayment: Extractor<VenmoPayment> = extract.shape({
  id: extract.string,
  amount: extract.string,
  date: extract.string,
  title: extract.shape({
    sender: extract.shape({
      username: extract.string,
      displayName: extract.string,
    }),
    receiver: extract.shape({
      username: extract.string,
      displayName: extract.string,
    }),
  }),
  note: extract.shape({content: extract.string}),
  type: extract.exact('payment'),
});

const paymentToResult = (payment: VenmoPayment): VenmoResult => {
  const other =
    payment.title.sender.username === 'danwanf'
      ? payment.title.receiver.displayName
      : payment.title.sender.displayName;
  const amount = parseVenmoAmount(payment.amount);
  const particle = amount >= 0 ? 'from' : 'to';
  return {
    amount,
    date: parseVenmoDate(payment.date),
    description: `Transfer ${particle} ${other}: ${payment.note.content}`,
    venmoId: payment.id,
  };
};

type VenmoTransfer = {
  id: string;
  amount: string;
  date: string;
  note: {name: string; lastFour: string};
  type: 'transfer';
};

const extractTransfer: Extractor<VenmoTransfer> = extract.shape({
  id: extract.string,
  amount: extract.string,
  date: extract.string,
  note: extract.shape({
    name: extract.string,
    lastFour: extract.string,
  }),
  type: extract.exact('transfer'),
});

const transferToResult = (transfer: VenmoTransfer): VenmoResult => {
  const amount = parseVenmoAmount(`- ${transfer.amount}`);
  console.log(transfer.amount, amount);
  return {
    amount,
    date: parseVenmoDate(transfer.date),
    description: `Bank transfer to ${transfer.note.name} ${transfer.note.lastFour}`,
    venmoId: transfer.id,
  };
};

const extractVenmo = extract.shape({
  stories: extract.array(extract.oneOf(extractPayment, extractTransfer)),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const venmoFromJson = (json: any): ExtractResult<VenmoResult[]> => {
  return extractVenmo(json).map((venmo) =>
    venmo.stories.map((transaction): VenmoResult => {
      switch (transaction.type) {
        case 'transfer':
          return transferToResult(transaction);
        case 'payment':
          return paymentToResult(transaction);
        default:
          return absurd(transaction);
      }
    }),
  );
};
const parseVenmo = (input: string): ExtractResult<VenmoResult[]> => {
  return parseJson(input)
    .map(venmoFromJson)
    .getOrElseL(() => failure(extractorError.invalidObject(input)));
};

export default parseVenmo;
